/**
 * Mobile API Wrappers for UI Components
 *
 * These wrappers provide mobile-style API (onPress, source, style arrays)
 * over the web-native UI Core components. This enables gradual migration
 * from mobile patterns to web patterns.
 *
 * @module @kidslabs/module-flash-sale/utils/mobile-wrappers
 */

import React, { forwardRef, type CSSProperties, type ReactNode } from 'react';
import {
    Button as UIButton,
    type ButtonProps as UIButtonProps,
    Image as UIImage,
    type ImageProps as UIImageProps,
    Text as UIText,
    type TextProps as UITextProps,
    Card as UICard,
    type CardProps as UICardProps,
    RadioButton as UIRadioButton,
    KidsLoading as UIKidsLoading,
    type TextSize,
} from '@kidslabs/mario-nextjs-react-ui-core';

// ============================================================================
// Type Utilities
// ============================================================================

/**
 * Flatten array of styles into single CSSProperties object
 */
const flattenStyle = (style?: CSSProperties | CSSProperties[]): CSSProperties | undefined => {
    if (!style) return undefined;
    if (Array.isArray(style)) {
        return Object.assign({}, ...style.filter(Boolean));
    }
    return style;
};

// ============================================================================
// Button Wrapper (onPress -> onClick)
// ============================================================================

export interface MobileButtonProps extends Omit<UIButtonProps, 'onClick'> {
    onPress?: () => void;
    style?: CSSProperties;
    labelStyle?: CSSProperties;
    loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, MobileButtonProps>(
    ({ onPress, style, children, ...props }, ref) => (
        <UIButton ref={ref} onClick={onPress} style={style} {...props}>
            {children}
        </UIButton>
    )
);
Button.displayName = 'Button';

// ============================================================================
// Image Wrapper (source -> src)
// ============================================================================

export interface MobileImageProps extends Omit<UIImageProps, 'src' | 'alt'> {
    source?: string | { uri: string };
    src?: string;
    alt?: string;
    resizeMode?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
    style?: CSSProperties;
}

export const Image: React.FC<MobileImageProps> = ({ source, src, alt = '', resizeMode = 'cover', style, ...props }) => {
    const imageSrc = src || (typeof source === 'string' ? source : source?.uri) || '';
    return <UIImage src={imageSrc} alt={alt} style={{ objectFit: resizeMode, ...style }} {...props} />;
};
Image.displayName = 'Image';

// ============================================================================
// Text Wrapper (testID, style arrays, xxlarge)
// ============================================================================

export interface MobileTextProps extends Omit<UITextProps, 'style' | 'size'> {
    testID?: string;
    style?: CSSProperties | CSSProperties[];
    size?: TextSize | 'xxlarge';
}

export const Text = forwardRef<HTMLElement, MobileTextProps>(({ testID, style, size, children, ...props }, ref) => {
    const flatStyle = flattenStyle(style);
    // Map xxlarge to xlarge (web doesn't have xxlarge)
    const mappedSize = size === 'xxlarge' ? 'xlarge' : size;
    return (
        <UIText ref={ref} data-testid={testID} style={flatStyle} size={mappedSize as TextSize} {...props}>
            {children}
        </UIText>
    );
});
Text.displayName = 'Text';

// ============================================================================
// Card Wrapper (mode -> variant)
// ============================================================================

export interface MobileCardProps extends Omit<UICardProps, 'variant'> {
    mode?: 'elevated' | 'outlined' | 'contained';
    variant?: UICardProps['variant'];
    style?: CSSProperties;
}

export const Card: React.FC<MobileCardProps> = ({ mode, variant, style, ...props }) => {
    let mappedVariant = variant;
    if (mode === 'elevated') {
        mappedVariant = 'elevated';
    } else if (mode === 'outlined') {
        mappedVariant = 'outlined';
    }
    return <UICard variant={mappedVariant} style={style} {...props} />;
};
Card.displayName = 'Card';

// ============================================================================
// RadioButton Wrapper (status, onPress, onValueChange, Android property)
// ============================================================================

export interface MobileRadioButtonProps {
    status?: 'checked' | 'unchecked';
    checked?: boolean;
    onPress?: () => void;
    onValueChange?: () => void;
    value?: string;
    disabled?: boolean;
    label?: string;
    className?: string;
    'data-testid'?: string;
    'aria-label'?: string;
}

const RadioButtonBase: React.FC<MobileRadioButtonProps> = ({
    status,
    checked,
    onPress,
    onValueChange,
    value = '',
    ...props
}) => {
    const isChecked = checked ?? status === 'checked';
    const handleChange = onValueChange || onPress;
    return <UIRadioButton checked={isChecked} onChange={handleChange} value={value} {...props} />;
};
RadioButtonBase.displayName = 'RadioButton';

export const RadioButton = Object.assign(RadioButtonBase, {
    Android: RadioButtonBase,
    IOS: RadioButtonBase,
});

// ============================================================================
// KidsLoading Wrapper (containerStyle)
// ============================================================================

export interface MobileKidsLoadingProps {
    containerStyle?: CSSProperties;
    size?: 'small' | 'large' | number;
}

export const KidsLoading: React.FC<MobileKidsLoadingProps> = ({ containerStyle, size = 'small' }) => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20, ...containerStyle }}>
        <UIKidsLoading size={size} />
    </div>
);
KidsLoading.displayName = 'KidsLoading';

// ============================================================================
// CouponListSkeleton Wrapper (layoutType)
// ============================================================================

export interface MobileCouponListSkeletonProps {
    layoutType?: string;
    count?: number;
}

export const CouponListSkeleton: React.FC<MobileCouponListSkeletonProps> = ({ count = 3 }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {Array.from({ length: count }).map((_, i) => (
            <div
                key={i}
                style={{ height: 80, backgroundColor: '#f0f0f0', borderRadius: 8, animation: 'pulse 1.5s infinite' }}
            />
        ))}
    </div>
);
CouponListSkeleton.displayName = 'CouponListSkeleton';

// ============================================================================
// CouponSection Wrapper (mobile props)
// ============================================================================

export interface MobileCouponSectionProps {
    coupons?: unknown[];
    selectedCoupon?: unknown;
    onSelectCoupon?: (coupon: unknown) => void;
    onCouponDetail?: (coupon: unknown) => void;
    CouponListHeaderComponent?: React.FC;
    emptyText?: string;
}

export const CouponSection: React.FC<MobileCouponSectionProps> = ({ coupons, onSelectCoupon, emptyText, ...props }) => (
    <div {...props}>
        {coupons && coupons.length > 0 ? (
            coupons.map((coupon, index) => (
                <div key={index} onClick={() => onSelectCoupon?.(coupon)} style={{ cursor: 'pointer' }}>
                    {/* Coupon item rendering would go here */}
                </div>
            ))
        ) : (
            <div style={{ textAlign: 'center', padding: 20, color: '#666' }}>{emptyText || 'Không có coupon'}</div>
        )}
    </div>
);
CouponSection.displayName = 'CouponSection';

// ============================================================================
// TextIcon Component (for coupon widget)
// ============================================================================

export interface TextIconProps {
    text?: string;
    rightIcon?: ReactNode;
    fontWeight?: string;
    fontSize?: string;
    children?: ReactNode;
}

export const TextIcon: React.FC<TextIconProps> = ({ text, rightIcon, fontWeight, fontSize, children }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight, fontSize }}>
        <span>{text || children}</span>
        {rightIcon}
    </div>
);
TextIcon.displayName = 'TextIcon';
